import { useMemo, useRef } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { handleImageDownload } from '../utils/handleImageDownload';

export interface ImageViewerProps {
  isOpen?: boolean;
  imageName: string;
  imageUrl: string;
  imageThumbnailUrl?: string;
  prevImageUrl?: string;
  prevImageThumbnailUrl?: string;
  nextImageUrl?: string;
  nextImageThumbnailUrl?: string;
  onPrevImageRequest?: () => void;
  onNextImageRequest?: () => void;
  onClose: () => void;
  onImageLoad?: () => void;
  onAfterOpen?: () => void;
}

/**
 * react-image-gallery를 사용한 현대적인 이미지 뷰어
 * React 18과 완벽 호환되며 라이트박스 기능과 썸네일 기능을 모두 제공
 */
export default function ImageViewer(props: ImageViewerProps) {
  const {
    isOpen = true,
    imageName,
    imageUrl,
    imageThumbnailUrl,
    prevImageUrl,
    prevImageThumbnailUrl,
    nextImageUrl,
    nextImageThumbnailUrl,
    onPrevImageRequest: _onPrevImageRequest,
    onNextImageRequest: _onNextImageRequest,
    onClose,
    onImageLoad,
    onAfterOpen,
  } = props;

  // react-image-gallery에서 사용할 이미지 배열 생성
  const images = useMemo(() => {
    const imageList: Array<{
      original: string;
      thumbnail: string;
      description?: string;
    }> = [];

    // 이전 이미지가 있으면 추가
    if (prevImageUrl) {
      imageList.push({
        original: prevImageUrl,
        thumbnail: prevImageThumbnailUrl || prevImageUrl,
        description: '이전 이미지',
      });
    }

    // 현재 이미지 추가
    imageList.push({
      original: imageUrl,
      thumbnail: imageThumbnailUrl || imageUrl,
      description: imageName,
    });

    // 다음 이미지가 있으면 추가
    if (nextImageUrl) {
      imageList.push({
        original: nextImageUrl,
        thumbnail: nextImageThumbnailUrl || nextImageUrl,
        description: '다음 이미지',
      });
    }

    return imageList;
  }, [
    imageUrl,
    imageName,
    imageThumbnailUrl,
    prevImageUrl,
    prevImageThumbnailUrl,
    nextImageUrl,
    nextImageThumbnailUrl,
  ]);

  // 현재 이미지의 시작 인덱스 (react-image-gallery 초기 표시용)
  const startIndex = useMemo(() => (prevImageUrl ? 1 : 0), [prevImageUrl]);

  const galleryRef = useRef<ImageGallery>(null);

  const handleDownloadClick = () => {
    const currentIndex = galleryRef.current?.getCurrentIndex() ?? startIndex;
    const currentItem = images[currentIndex];
    if (currentItem) {
      handleImageDownload([
        {
          url: currentItem.original,
          filename: imageName || 'image',
        },
      ]);
    }
  };

  if (!isOpen || images.length === 0) {
    return null;
  }

  // 이미지 로드 완료 핸들러
  const handleImageLoad = () => {
    onImageLoad?.();
    onAfterOpen?.();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-label="이미지 뷰어"
      className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center"
      onClick={(e) => {
        // 배경 클릭시 닫기
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        // ESC 키로 닫기
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      tabIndex={-1}
    >
      <div className="relative w-full h-full max-w-4xl max-h-4xl">
        <button
          className="absolute top-4 right-16 z-10 text-white text-xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-all"
          onClick={handleDownloadClick}
          aria-label="다운로드"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M12 3v13.5M8.25 12.75 12 16.5l3.75-3.75"
            />
          </svg>
        </button>

        {/* 닫기 버튼 */}
        <button
          className="absolute top-4 right-4 z-10 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-all"
          onClick={onClose}
          aria-label="닫기"
        >
          ×
        </button>

        <ImageGallery
          ref={galleryRef}
          items={images}
          startIndex={startIndex}
          showThumbnails={images.length > 1}
          showNav={images.length > 1}
          showPlayButton={false}
          showFullscreenButton={true}
          showBullets={false}
          useBrowserFullscreen={true}
          onImageLoad={handleImageLoad}
          additionalClass="custom-image-gallery"
        />
      </div>
    </div>
  );
}
